import { getUserAgnosticGlobalFeatureGatesFromMemory } from '@src/modules/global-state/user-agnostic-feature-gates.browser'
import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'
import { forEachTelemetryIntegration } from '@src/telemetry/telemetry-registry'

import { TelemetryEventDataMap } from './telemetry-events.types'

type FeatureTag = (typeof FEATURES)[keyof typeof FEATURES]

/**
 * Per-emit knobs.
 *
 * - `only` / `except` are string-id allow/deny lists. Integration ids are
 *   the keys returned by `TELEMETRY_INTEGRATION_REGISTRY_MAP_GETTER` in
 *   `telemetry-registry.ts` (e.g. `'internalAnalytics'`, `'mixpanel'`,
 *   `'googleAnalytics'`). If both are provided, an integration must satisfy
 *   `only` AND not be in `except` to receive the event.
 * - `shouldGenerateEventId` controls whether an `eventId` is added to the
 *   payload before fan-out. Defaults to `true`.
 * - `feature` is the Sentry feature tag used if the emit throws. Defaults
 *   to `FEATURES.TRACK_EVENT`. Set this when wrapping `track()` from a
 *   higher-level API so the Sentry breadcrumb names the right surface.
 */
export interface TrackOptions {
  only?: readonly string[]
  except?: readonly string[]
  shouldGenerateEventId?: boolean
  feature?: FeatureTag
}

const S4 = () => (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1)

export const generateUniqueEventId = () => {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition, @typescript-eslint/prefer-optional-chain
  if (crypto && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  return (S4() + S4() + '-' + S4() + '-4' + S4().substr(0, 3) + '-' + S4() + '-' + S4() + S4() + S4()).toLowerCase()
}

// sss

const getEnhancedProperties = (properties: unknown) => {
  return {
    eventId: generateUniqueEventId(),
    ...(properties as NonNullable<unknown>),
  }
}

/**
 * Unified emit API. Fans out to every registered integration that handles
 * `event`, honouring `options.only` / `options.except` and any feature-gate
 * / consent rules baked into `forEachTelemetryIntegration`. Equivalent to
 * `trackEvent` for the default case, plus per-call routing scope.
 *
 * The `payload` type comes from `TelemetryEventDataMap[T]`. Schema adoption
 * happens at the event level: an event becomes schema-anchored by aliasing
 * its entry to `Web<EventName>['context']` from `@lastbrand/event-schemas`
 * (see ADD_TO_CART for the pattern). Call sites and handlers both type-check
 * against the canonical context — no separate override needed.
 */
export function track<T extends keyof TelemetryEventDataMap>(
  event: T,
  payload: TelemetryEventDataMap[T],
  options?: TrackOptions,
) {
  try {
    const enhancedProperties =
      options?.shouldGenerateEventId === false ? payload : (getEnhancedProperties(payload) as TelemetryEventDataMap[T])
    const only = options?.only
    const except = options?.except
    forEachTelemetryIntegration((integrationKey, integration) => {
      if (only && !only.includes(integrationKey)) return
      if (except?.includes(integrationKey)) return
      const handler = integration.events?.[event]
      if (!handler) return
      const result = handler(enhancedProperties)
      if (result && typeof (result as PromiseLike<unknown>).then === 'function') {
        ;(result as PromiseLike<unknown>).then(undefined, () => {
          // ignore handler rejections — matches trackEvent's historical behavior
        })
      }
    }, getUserAgnosticGlobalFeatureGatesFromMemory())
  } catch (e) {
    captureException(e, {
      module: MODULES.EVENT_TRACKING,
      feature: options?.feature ?? FEATURES.TRACK_EVENT,
    })
  }
}

export function trackEvent<T extends keyof TelemetryEventDataMap>(event: T, data: unknown) {
  track(event, data as TelemetryEventDataMap[T])
}

export function trackGAEvent<T extends keyof TelemetryEventDataMap>(
  event: T,
  data: unknown,
  shouldGenerateEventId = true,
) {
  track(event, data as TelemetryEventDataMap[T], {
    only: ['googleAnalytics'],
    shouldGenerateEventId,
    feature: FEATURES.TRACK_GA,
  })
}

export function trackInternalEvent<T extends keyof TelemetryEventDataMap>(event: T, data: unknown) {
  track(event, data as TelemetryEventDataMap[T], {
    only: ['internalAnalytics'],
    feature: FEATURES.TRACK_INTERNAL_ANALYTICS,
  })
}

export function trackClickEvent<T extends keyof TelemetryEventDataMap>(event: T, data: Record<string, unknown>) {
  const { href: pageUrl, pathname } = window.location
  if (data.trackGA) {
    trackGAEvent(event, {
      ...data,
      pageUrl,
      pathname,
    })
  }
  if (data.trackInternal) {
    trackInternalEvent(event, {
      ...data,
      pageUrl,
      pathname,
    })
  }
}

export function initializeTelemetry() {
  forEachTelemetryIntegration(async (_integrationKey, integration) => {
    await integration.initialize?.()
  }, getUserAgnosticGlobalFeatureGatesFromMemory())
}
